import {
  ATTACH_KINDS,
  DEFAULT_SKILL_MODS,
  EQUIP_SLOTS,
  LIMB_IDS,
  MAX_GRID_DEPTH,
  MAX_MOD_DEPTH,
  MAX_NAME_LENGTH,
  PROTOCOL_VERSION,
  decodeMsg,
  encodeMsg,
  getItemDef,
  isBarkKind,
  isRaidEnv,
  isTrackKind,
  isValidUsername,
  sanitizeHideout,
  sanitizeLoyalty,
  sanitizeProgress,
  type ClientMsg,
  type ContainerKind,
  type DeathCause,
  type EntitySnap,
  type EquipmentRecord,
  type FriendEntry,
  type FriendRef,
  type GameEvent,
  type GlassAct,
  type GridRect,
  type GridState,
  type InputCmd,
  type Inventory,
  type InventoryOp,
  type ItemInstance,
  type LimbId,
  type MapSource,
  type PartyMemberWire,
  type PlacedItem,
  type ProfileWire,
  type QuestState,
  type RaidResult,
  type RaidSummary,
  type Rotation,
  type ServerMsg,
  type SkillMods,
  type Snapshot,
  type SocialPhase,
  type TrackSnap,
  type Vec3,
  type WeaponEquipSlot,
  type WildlifeSpecies,
  type YouSnap,
} from "@rietz/shared";

/**
 * Thin, typed WebSocket wrapper: connection lifecycle (connect, reconnect with
 * backoff), the shared codec, and narrowing every inbound frame to a real
 * `ServerMsg`. Holds no game state.
 */

export type NetStatus = "idle" | "connecting" | "open" | "closed";

export type WelcomeMsg = Extract<ServerMsg, { t: "welcome" }>;
export type PongMsg = Extract<ServerMsg, { t: "pong" }>;
export type ErrorMsg = Extract<ServerMsg, { t: "error" }>;
export type ContainerStateMsg = Extract<ServerMsg, { t: "containerState" }>;
export type InvResultMsg = Extract<ServerMsg, { t: "invResult" }>;
export type WorldItemsMsg = Extract<ServerMsg, { t: "worldItems" }>;
/** One item lying on the ground, as `worldItems` and `itemDropped` carry it. */
export type WorldItemSnap = WorldItemsMsg["items"][number];
export type QueueStatusMsg = Extract<ServerMsg, { t: "queueStatus" }>;
export type FriendsMsg = Extract<ServerMsg, { t: "friends" }>;
export type PartyStateMsg = Extract<ServerMsg, { t: "partyState" }>;
export type PartyInvitedMsg = Extract<ServerMsg, { t: "partyInvited" }>;

/** The `fire` payload, minus the discriminant the socket fills in. */
export type FireShot = Omit<Extract<ClientMsg, { t: "fire" }>, "t">;

/** The `grenadeThrow` payload, same shape of thing as `FireShot`. */
export type GrenadeToss = Omit<Extract<ClientMsg, { t: "grenadeThrow" }>, "t">;

export interface SocketHandlers {
  onStatus: (status: NetStatus) => void;
  onWelcome: (msg: WelcomeMsg) => void;
  onSnapshot: (snap: Snapshot) => void;
  onEvent: (ev: GameEvent) => void;
  onPong: (msg: PongMsg) => void;
  onServerError: (msg: ErrorMsg) => void;
  /**
   * You were flashed. Per-recipient rather than an event; carries only the
   * duration.
   */
  onFlash: (sec: number) => void;
  /** The authoritative inventory, whole. Never a delta — see the protocol. */
  onInv: (inv: Inventory) => void;
  onContainerState: (msg: ContainerStateMsg) => void;
  onInvResult: (msg: InvResultMsg) => void;
  onWorldItems: (items: readonly WorldItemSnap[]) => void;
  /** Panes already broken when we joined. Only sent when the list is non-empty. */
  onGlassState: (broken: readonly number[]) => void;
  // --- Meta. Which of these fires first tells the client the server's kind: a
  // meta server answers `hello` with `profile`, a direct one with `welcome`.

  /** THE meta state, whole. Never a delta; replaces whatever we believed. */
  onProfile: (profile: ProfileWire) => void;
  onQueueStatus: (msg: QueueStatusMsg) => void;
  /** The raid is over. A `profile` with the consequences follows. */
  onRaidEnd: (summary: RaidSummary) => void;

  // --- Friends and the party: pushed state, never answers (a verb's verdict is
  // the `invResult` above).

  /** THE friends state, whole: the list and both halves of the pending requests. */
  onFriends: (msg: FriendsMsg) => void;
  /** THE party, whole, or `null` when there is none. */
  onPartyState: (party: PartyStateMsg["party"]) => void;
  /** Somebody is asking us into their squad. Replaces any earlier prompt. */
  onPartyInvited: (from: FriendRef) => void;
}

/**
 * Ceiling on how long a `flash` may claim — paranoia about the one field that
 * paints over the whole screen.
 */
const MAX_FLASH_SEC = 10;

const RECONNECT_BASE_MS = 2000;
const RECONNECT_MAX_MS = 10000;

export interface GameSocketOptions {
  url: string;
  name: string;
}

export class GameSocket {
  readonly url: string;

  private readonly name: string;
  private ws: WebSocket | null = null;
  private status: NetStatus = "idle";
  private attempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private disposed = false;

  private readonly handlers: Partial<SocketHandlers> = {};

  constructor(opts: GameSocketOptions) {
    this.url = opts.url;
    this.name = opts.name;
  }

  on<K extends keyof SocketHandlers>(event: K, fn: SocketHandlers[K]): void {
    this.handlers[event] = fn;
  }

  getStatus(): NetStatus {
    return this.status;
  }

  isOpen(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  connect(): void {
    if (this.disposed) return;
    if (this.ws !== null) return;

    this.setStatus("connecting");

    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.addEventListener("open", () => {
      if (this.ws !== ws) return;
      this.attempt = 0;
      this.setStatus("open");
      // The server expects `hello` as the very first frame on every connection,
      // including reconnects — it is what allocates the player slot.
      this.send({
        t: "hello",
        protocol: PROTOCOL_VERSION,
        name: this.name,
        // Meta servers authenticate the HttpOnly cookie from the WebSocket
        // upgrade. Kept on the wire only for direct/older server compatibility.
        token: "",
      });
    });

    ws.addEventListener("message", (ev: MessageEvent<unknown>) => {
      if (this.ws !== ws) return;
      const raw = ev.data;
      if (
        typeof raw !== "string" &&
        !(raw instanceof ArrayBuffer) &&
        !(raw instanceof Uint8Array)
      ) {
        return;
      }
      const decoded = decodeMsg(raw);
      const msg = asServerMsg(decoded);
      if (msg === null) return;
      this.dispatch(msg);
    });

    ws.addEventListener("close", () => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.setStatus("closed");
      this.scheduleReconnect();
    });

    ws.addEventListener("error", () => {
      // `error` is always followed by `close`; the reconnect is handled there.
    });
  }

  send(msg: ClientMsg): boolean {
    const ws = this.ws;
    if (ws === null || ws.readyState !== WebSocket.OPEN) return false;
    ws.send(encodeMsg(msg));
    return true;
  }

  sendInput(cmds: InputCmd[]): boolean {
    if (cmds.length === 0) return false;
    return this.send({ t: "input", cmds });
  }

  sendWeaponSelect(slot: WeaponEquipSlot): boolean {
    return this.send({ t: "weaponSelect", slot });
  }

  sendPing(cNow: number): boolean {
    return this.send({ t: "ping", cNow });
  }

  /**
   * One trigger pull. Fire-and-forget: a shot replayed a second later would
   * resolve against a world the shooter never saw.
   */
  sendFire(shot: FireShot): boolean {
    return this.send({
      t: "fire",
      tick: shot.tick,
      renderTick: shot.renderTick,
      origin: shot.origin,
      dir: shot.dir,
    });
  }

  sendRespawn(): boolean {
    return this.send({ t: "respawn" });
  }

  /**
   * Pull the pin. Fire-and-forget; if lost, the throw is refused ("not-armed")
   * and the grenade stays in the pocket — the failure worth having.
   */
  sendGrenadeArm(iid: string): boolean {
    return this.send({ t: "grenadeArm", iid });
  }

  /** Let go of the armed grenade. Same geometry as `sendFire`. */
  sendGrenadeThrow(toss: GrenadeToss): boolean {
    return this.send({
      t: "grenadeThrow",
      tick: toss.tick,
      origin: toss.origin,
      dir: toss.dir,
    });
  }

  // --- Inventory. All fire-and-forget: the change is already applied
  // optimistically and a lost message is corrected by the next `inv`.
  // Queueing would replay a drag against a rearranged inventory.

  /** One inventory edit. `opId` is echoed back in `invResult`. */
  sendInvOp(opId: number, op: InventoryOp): boolean {
    return this.send({ t: "invOp", opId, op });
  }

  /** Start looking inside a container. Answered with `containerState`. */
  sendOpen(containerId: string): boolean {
    return this.send({ t: "open", containerId });
  }

  sendClose(containerId: string): boolean {
    return this.send({ t: "close", containerId });
  }

  /** Take a dropped item off the ground; the server auto-places it. */
  sendPickup(itemId: string): boolean {
    return this.send({ t: "pickup", itemId });
  }

  /**
   * Smash a window pane, or climb through a broken one. `id` indexes
   * `map.glass`. Fire-and-forget: the `glass` event is the only answer, and a
   * refused request simply never happened.
   */
  sendGlass(id: number, act: GlassAct): boolean {
    return this.send({ t: "glass", id, act });
  }

  /** Begin using a med. The target limb is resolved server-side. */
  sendUseMed(iid: string): boolean {
    return this.send({ t: "useMed", iid });
  }

  /**
   * Look into the magazine. No payload — the server knows the gun in hand. A
   * lost check simply never happened; nothing was predicted.
   */
  sendMagCheck(): boolean {
    return this.send({ t: "magCheck" });
  }

  // --- Meta. Fire-and-forget; a lost purchase never happened, and the next
  // `profile` says so — which is why the client never edits its own euros.

  /** Put me in the queue for the next raid. Answered with `queueStatus`. */
  sendQueueJoin(): boolean {
    return this.send({ t: "queueJoin" });
  }

  sendQueueLeave(): boolean {
    return this.send({ t: "queueLeave" });
  }

  /**
   * Buy one offer. `opId` comes from the same counter as `invOp`
   * (`game/opIds.ts`) — one id space, one answer type.
   */
  sendBuy(opId: number, traderId: string, defId: string): boolean {
    return this.send({ t: "buy", opId, traderId, defId });
  }

  /** Sell one instance out of the STASH. Same id space as `sendBuy`. */
  sendSell(opId: number, traderId: string, iid: string): boolean {
    return this.send({ t: "sell", opId, traderId, iid });
  }

  /** Repair one armour instance, wherever the account owns it. No trader is
   *  named — the bench belongs to nobody. Same id space as `sendBuy`. */
  sendRepair(opId: number, iid: string): boolean {
    return this.send({ t: "repair", opId, iid });
  }

  /**
   * Hand instances over against ONE quest. Same id space as `sendBuy`. The
   * ORDER of `iids` is meaningful — the server walks it as sent.
   */
  sendQuestTurnIn(opId: number, questId: string, iids: readonly string[]): boolean {
    return this.send({ t: "questTurnIn", opId, questId, iids: [...iids] });
  }

  // --- The hideout. Same id space and answer shape as `buy`/`sell`. No
  // timestamps — time is the server's.

  /** Start the next level of a module. Costs are taken immediately. */
  sendHideoutBuild(opId: number, moduleId: string): boolean {
    return this.send({ t: "hideoutBuild", opId, moduleId });
  }

  /** Start a recipe. Inputs leave the stash now, the output on collection. */
  sendHideoutCraft(opId: number, craftId: string): boolean {
    return this.send({ t: "hideoutCraft", opId, craftId });
  }

  /** Take a finished craft's output into the stash. Can fail on space. */
  sendHideoutCollect(opId: number, craftId: string): boolean {
    return this.send({ t: "hideoutCollect", opId, craftId });
  }

  /** Empty a production module's buffer into the stash. Can fail on space. */
  sendHideoutHarvest(opId: number, moduleId: string): boolean {
    return this.send({ t: "hideoutHarvest", opId, moduleId });
  }

  /** Feed ONE canister — an instance in the stash — into the tank. */
  sendHideoutFuel(opId: number, iid: string): boolean {
    return this.send({ t: "hideoutFuel", opId, iid });
  }

  /** The generator's switch. */
  sendHideoutPower(opId: number, on: boolean): boolean {
    return this.send({ t: "hideoutPower", opId, on });
  }

  /** One finished session on the gym's bench: which skill, and how well it went
   *  in [0, 1]. The cooldown is the server's and no time is sent. */
  sendHideoutTrain(opId: number, skill: string, score: number): boolean {
    return this.send({ t: "hideoutTrain", opId, skill, score });
  }

  // --- Friends and the party. Same id space; the resulting state is PUSHED
  // afterwards. Nothing is predicted (`game/social.ts`), so a lost message
  // leaves the client showing what the server last said. Everyone is named by
  // USERNAME (display names are neither unique nor stable).

  /** Ask `username` to be friends (or re-send a request already outstanding). */
  sendFriendRequest(opId: number, username: string): boolean {
    return this.send({ t: "friendRequest", opId, username });
  }

  /** Answer an INCOMING request — accept it, or drop it silently. */
  sendFriendRespond(opId: number, username: string, accept: boolean): boolean {
    return this.send({ t: "friendRespond", opId, username, accept });
  }

  /** Unfriend, or withdraw a request we sent. One verb for both gestures. */
  sendFriendRemove(opId: number, username: string): boolean {
    return this.send({ t: "friendRemove", opId, username });
  }

  /** Ask a friend into the party, creating one (with us as leader) if needed. */
  sendPartyInvite(opId: number, username: string): boolean {
    return this.send({ t: "partyInvite", opId, username });
  }

  /** Answer THE pending invite. There is at most one, so it needs no name. */
  sendPartyRespond(opId: number, accept: boolean): boolean {
    return this.send({ t: "partyRespond", opId, accept });
  }

  /** Walk out. Leadership passes on; the party dies with its last member. */
  sendPartyLeave(opId: number): boolean {
    return this.send({ t: "partyLeave", opId });
  }

  /** Throw a member out. Leader only, and never oneself — that is `leave`. */
  sendPartyKick(opId: number, username: string): boolean {
    return this.send({ t: "partyKick", opId, username });
  }

  dispose(): void {
    this.disposed = true;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    const ws = this.ws;
    this.ws = null;
    if (ws !== null) ws.close();
    this.setStatus("closed");
  }

  // -----------------------------------------------------------------------

  private dispatch(msg: ServerMsg): void {
    switch (msg.t) {
      case "welcome":
        this.handlers.onWelcome?.(msg);
        return;
      case "snapshot":
        this.handlers.onSnapshot?.(msg.snap);
        return;
      case "event":
        this.handlers.onEvent?.(msg.ev);
        return;
      case "pong":
        this.handlers.onPong?.(msg);
        return;
      case "error":
        this.handlers.onServerError?.(msg);
        return;
      case "flash":
        this.handlers.onFlash?.(msg.sec);
        return;
      case "inv":
        this.handlers.onInv?.(msg.inv);
        return;
      case "containerState":
        this.handlers.onContainerState?.(msg);
        return;
      case "invResult":
        this.handlers.onInvResult?.(msg);
        return;
      case "worldItems":
        this.handlers.onWorldItems?.(msg.items);
        return;
      case "glassState":
        this.handlers.onGlassState?.(msg.broken);
        return;
      case "profile":
        this.handlers.onProfile?.(msg.profile);
        return;
      case "queueStatus":
        this.handlers.onQueueStatus?.(msg);
        return;
      case "raidEnd":
        this.handlers.onRaidEnd?.(msg.summary);
        return;
      case "friends":
        this.handlers.onFriends?.(msg);
        return;
      case "partyState":
        this.handlers.onPartyState?.(msg.party);
        return;
      case "partyInvited":
        this.handlers.onPartyInvited?.(msg.from);
        return;
      default:
        return;
    }
  }

  private setStatus(status: NetStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.handlers.onStatus?.(status);
  }

  private scheduleReconnect(): void {
    if (this.disposed) return;
    if (this.reconnectTimer !== null) return;
    this.attempt += 1;
    const delay = Math.min(RECONNECT_BASE_MS * this.attempt, RECONNECT_MAX_MS);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }
}

// ---------------------------------------------------------------------------
// Inbound narrowing. Anything that does not match the contract exactly is
// dropped, never coerced — a malformed snapshot must not poison prediction.
// ---------------------------------------------------------------------------

function isObj(x: unknown): x is Record<string, unknown> {
  return typeof x === "object" && x !== null && !Array.isArray(x);
}

function num(x: unknown): number | null {
  return typeof x === "number" && Number.isFinite(x) ? x : null;
}

function vec3(x: unknown): Vec3 | null {
  if (!isObj(x)) return null;
  const vx = num(x["x"]);
  const vy = num(x["y"]);
  const vz = num(x["z"]);
  if (vx === null || vy === null || vz === null) return null;
  return { x: vx, y: vy, z: vz };
}

function stance(x: unknown): "stand" | "crouch" | null {
  return x === "stand" || x === "crouch" ? x : null;
}

function anim(x: unknown): EntitySnap["anim"] | null {
  return x === "idle" || x === "walk" || x === "sprint" || x === "dead"
    ? x
    : null;
}

function kind(x: unknown): EntitySnap["kind"] | null {
  return x === "player" || x === "scav" || x === "animal" ? x : null;
}

/**
 * `EntitySnap.species`, one of exactly three strings. Narrowed because it picks
 * the rig; `null` drops the entity.
 */
function species(x: unknown): WildlifeSpecies | null {
  return x === "hare" || x === "deer" || x === "chamois" ? x : null;
}

function bool(x: unknown): boolean | null {
  return typeof x === "boolean" ? x : null;
}

/** A non-negative safe integer, e.g. a grid coordinate or a stack size. */
function uint(x: unknown): number | null {
  return typeof x === "number" && Number.isSafeInteger(x) && x >= 0 ? x : null;
}

/** A non-empty string. Ids and def ids are never blank on the wire. */
function id(x: unknown): string | null {
  return typeof x === "string" && x.length > 0 ? x : null;
}

/**
 * A limb bitmask; bits outside `LIMB_IDS` are a frame from a protocol we do not
 * speak.
 */
const LIMB_MASK_ALL = (1 << LIMB_IDS.length) - 1;

function limbMask(x: unknown): number | null {
  if (typeof x !== "number" || !Number.isInteger(x)) return null;
  return x >= 0 && x <= LIMB_MASK_ALL ? x : null;
}

/** `YouSnap.hp`: exactly seven finite numbers, in `LIMB_IDS` order. */
function limbHp(x: unknown): number[] | null {
  if (!Array.isArray(x) || x.length !== LIMB_IDS.length) return null;
  const out = new Array<number>(x.length);
  for (let i = 0; i < x.length; i++) {
    const v = num(x[i]);
    // Negative hp is not a thing the server can produce; treating it as
    // malformed keeps the HUD from ever having to render a nonsense limb.
    if (v === null || v < 0) return null;
    out[i] = v;
  }
  return out;
}

function weapon(x: unknown): YouSnap["weapon"] | null {
  if (!isObj(x)) return null;
  const mag = num(x["mag"]);
  const reloading = bool(x["reloading"]);
  if (mag === null || mag < 0 || reloading === null) return null;
  return { mag, reloading };
}

function limbId(x: unknown): LimbId | null {
  // A tiny linear scan over seven strings, only ever on a `hit` event.
  for (const id of LIMB_IDS) {
    if (x === id) return id;
  }
  return null;
}

const DEATH_CAUSES: readonly DeathCause[] = ["shot", "blast", "bleed", "starve"];

/** REQUIRED on a `death`: an unknown cause is a protocol we do not speak. */
function deathCause(x: unknown): DeathCause | null {
  for (const c of DEATH_CAUSES) {
    if (x === c) return c;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Inventory narrowing. An inventory that is not exactly right is DROPPED, not
// repaired — `applyInventoryOp` is fed straight from it. The previous `inv`
// stays valid and another is a resync away.
// ---------------------------------------------------------------------------

/** The map's container kinds, plus the corpse pseudo-kind. */
const CONTAINER_KINDS: readonly (ContainerKind | "corpse")[] = [
  "crate",
  "cupboard",
  "shelf",
  "toolbox",
  "fridge",
  "chest",
  "corpse",
];

function containerKind(x: unknown): ContainerKind | "corpse" | null {
  for (const k of CONTAINER_KINDS) {
    if (x === k) return k;
  }
  return null;
}

/** One instance in a TOP-LEVEL grid — depth 0, mods allowed. */
function itemInstance(x: unknown): ItemInstance | null {
  return itemInstanceAt(x, 0, 0);
}

/**
 * One instance off the wire, field by field. Two independent depths:
 * `modDepth` (how far down the part tree; bounds the `mods` recursion at
 * `MAX_MOD_DEPTH` — a fitted magazine still decodes its `mag`/`ammoDefId`)
 * and `depth` (the grid this sits in; bounds `contents` at `MAX_GRID_DEPTH`).
 * A part inherits its host's grid depth — it is not in a grid.
 */
function itemInstanceAt(x: unknown, modDepth: number, depth: number): ItemInstance | null {
  if (!isObj(x)) return null;
  const iid = id(x["iid"]);
  const defId = id(x["defId"]);
  const stack = uint(x["stack"]);
  if (iid === null || defId === null || stack === null || stack < 1) return null;

  const out: ItemInstance = { iid, defId, stack };

  // Optional scalars are all-or-nothing: malformed rejects, absent is absent.
  // `durability` means three things by def, so it is only bounded below.
  const durability = x["durability"];
  if (durability !== undefined) {
    const v = num(durability);
    if (v === null || v < 0) return null;
    out.durability = v;
  }
  // The repaired-down ceiling; strictly above zero because `applyRepair` floors
  // it at 1 and everything downstream divides by it.
  const maxDurability = x["maxDurability"];
  if (maxDurability !== undefined) {
    const v = num(maxDurability);
    if (v === null || !(v > 0)) return null;
    out.maxDurability = v;
  }
  const mag = x["mag"];
  if (mag !== undefined) {
    const v = uint(mag);
    if (v === null) return null;
    out.mag = v;
  }
  const ammoDefId = x["ammoDefId"];
  if (ammoDefId !== undefined) {
    const v = id(ammoDefId);
    if (v === null) return null;
    out.ammoDefId = v;
  }

  // `mods` is REPAIRED rather than rejected: an unrecognised entry makes the
  // gun a gun without that part (as `aggregateWeaponStats` treats it), and
  // dropping the whole inventory over a newer content version would be worse.
  const mods = x["mods"];
  if (mods !== undefined && modDepth < MAX_MOD_DEPTH && isObj(mods)) {
    let fitted: { [slotId: string]: ItemInstance } | null = null;
    for (const slot of ATTACH_KINDS) {
      const raw = mods[slot];
      if (raw === undefined) continue;
      // One level down the PART tree, same grid depth. A part at
      // `MAX_MOD_DEPTH`
      // decodes but loses any `mods` of its own.
      const part = itemInstanceAt(raw, modDepth + 1, depth);
      if (part === null) continue;
      if (fitted === null) fitted = {};
      fitted[slot] = part;
    }
    // An empty map is left OFF, so a stripped gun decodes like an unmodified
    // one.
    if (fitted !== null) out.mods = fitted;
  }

  // `contents` is REPAIRED like `mods`. Believed only if the def is a known
  // container, the grid decodes, the cap holds and something is inside — an
  // empty grid decodes to no key (absent-means-empty).
  const contents = x["contents"];
  if (contents !== undefined && depth < MAX_GRID_DEPTH) {
    const def = getItemDef(defId);
    if (def !== null && def.kind === "container") {
      const grid = gridStateAt(contents, depth + 1);
      if (grid !== null && grid.items.length > 0) out.contents = grid;
    }
  }
  return out;
}

function placedItemAt(x: unknown, depth: number): PlacedItem | null {
  if (!isObj(x)) return null;
  const item = itemInstanceAt(x["item"], 0, depth);
  const px = uint(x["x"]);
  const py = uint(x["y"]);
  const rot = x["rot"];
  if (item === null || px === null || py === null) return null;
  if (rot !== 0 && rot !== 1) return null;
  return { item, x: px, y: py, rot: rot as Rotation };
}

/** A top-level grid: pockets, a worn bag's capacity, a crate, the stash. */
function gridState(x: unknown): GridState | null {
  return gridStateAt(x, 0);
}

/**
 * A grid's pouches, or `null` when the array is malformed. Absent is legal
 * (one open compartment); present and wrong is refused, since half a layout
 * would draw walls in the wrong places. The caller makes that a dead grid.
 */
function gridZones(x: unknown, w: number, h: number): readonly GridRect[] | null {
  if (!Array.isArray(x)) return null;
  const zones: GridRect[] = [];
  for (const raw of x) {
    if (!isObj(raw)) return null;
    const zx = uint(raw["x"]);
    const zy = uint(raw["y"]);
    const zw = uint(raw["w"]);
    const zh = uint(raw["h"]);
    if (zx === null || zy === null || zw === null || zh === null) return null;
    if (zw < 1 || zh < 1) return null;
    if (zx + zw > w || zy + zh > h) return null;
    zones.push({ x: zx, y: zy, w: zw, h: zh });
  }
  return zones;
}

/** `depth` is this grid's own depth — 0 at top level, 1 inside a box, and so
 *  on up to `MAX_GRID_DEPTH`. It is only ever threaded further down. */
function gridStateAt(x: unknown, depth: number): GridState | null {
  if (!isObj(x)) return null;
  const w = uint(x["w"]);
  const h = uint(x["h"]);
  const itemsRaw = x["items"];
  if (w === null || w < 1 || h === null || h < 1) return null;
  if (!Array.isArray(itemsRaw)) return null;

  const zonesRaw = x["zones"];
  const zones = zonesRaw === undefined ? null : gridZones(zonesRaw, w, h);
  if (zonesRaw !== undefined && zones === null) return null;

  const items: PlacedItem[] = [];
  for (const raw of itemsRaw) {
    const p = placedItemAt(raw, depth);
    // One bad placement poisons the grid — the player would drag onto an
    // occupied cell. Inside a container the host keeps its place and loses the
    // subtree.
    if (p === null) return null;
    items.push(p);
  }
  // The key goes on only when it was sent, which is what keeps a decoded grid
  // deep-equal to the one the server holds (`cloneGrid` obeys the same rule).
  const grid: GridState = { w, h, items };
  if (zones !== null) grid.zones = zones;
  return grid;
}

/** A grid that may legitimately be absent — no rig worn, no backpack. */
function optionalGrid(x: unknown): { ok: true; grid: GridState | null } | null {
  if (x === null || x === undefined) return { ok: true, grid: null };
  const grid = gridState(x);
  return grid === null ? null : { ok: true, grid };
}

/**
 * The seven worn slots, built from `EQUIP_SLOTS` so an extra key cannot smuggle
 * an eighth in. Shared by `inv` and a body's `equipment`.
 */
function equipmentRecord(x: unknown): EquipmentRecord | null {
  if (!isObj(x)) return null;
  const equipment = {} as EquipmentRecord;
  for (const slot of EQUIP_SLOTS) {
    const raw = x[slot];
    if (raw === null || raw === undefined) {
      equipment[slot] = null;
      continue;
    }
    const item = itemInstance(raw);
    if (item === null) return null;
    equipment[slot] = item;
  }
  return equipment;
}

function inventory(x: unknown): Inventory | null {
  if (!isObj(x)) return null;
  const equipment = equipmentRecord(x["equipment"]);
  if (equipment === null) return null;

  const pockets = gridState(x["pockets"]);
  if (pockets === null) return null;
  const rig = optionalGrid(x["rig"]);
  const backpack = optionalGrid(x["backpack"]);
  if (rig === null || backpack === null) return null;

  return { equipment, pockets, rig: rig.grid, backpack: backpack.grid };
}

/**
 * `EntitySnap.gear`. Optional on the wire (`undefined` = default silhouette);
 * present and malformed rejects the entity.
 */
function entityGear(x: unknown): { ok: true; gear: EntitySnap["gear"] } | null {
  if (x === undefined) return { ok: true, gear: undefined };
  if (!isObj(x)) return null;
  const weaponRaw = x["weapon"];
  const weapon = weaponRaw === null ? null : id(weaponRaw);
  if (weaponRaw !== null && weapon === null) return null;
  const vest = bool(x["vest"]);
  const helmet = bool(x["helmet"]);
  const backpack = bool(x["backpack"]);
  if (vest === null || helmet === null || backpack === null) return null;
  return { ok: true, gear: { weapon, vest, helmet, backpack } };
}

function entitySnap(x: unknown): EntitySnap | null {
  if (!isObj(x)) return null;
  const eid = id(x["id"]);
  if (eid === null) return null;
  const k = kind(x["kind"]);
  const pos = vec3(x["pos"]);
  const yaw = num(x["yaw"]);
  const pitch = num(x["pitch"]);
  const st = stance(x["stance"]);
  const an = anim(x["anim"]);
  if (
    k === null ||
    pos === null ||
    yaw === null ||
    pitch === null ||
    st === null ||
    an === null
  ) {
    return null;
  }
  const gear = entityGear(x["gear"]);
  if (gear === null) return null;

  const out: EntitySnap = {
    id: eid,
    kind: k,
    pos,
    yaw,
    pitch,
    stance: st,
    anim: an,
  };
  const name = x["name"];
  if (typeof name === "string") out.name = name;
  if (gear.gear !== undefined) out.gear = gear.gear;

  // Reload: absent means not reloading (the server omits false); present and
  // not a boolean drops this entity. Stored only when true, so the shape is
  // byte-for-byte what was sent.
  const reloadRaw = x["reload"];
  if (reloadRaw !== undefined) {
    const reload = bool(reloadRaw);
    if (reload === null) return null;
    if (reload) out.reload = true;
  }

  // Lean, like `reload`: absent is upright. Accepted set is EXACTLY {−1, 1} —
  // a 0 should have been omitted, and the answer moves hitboxes.
  const leanRaw = x["lean"];
  if (leanRaw !== undefined) {
    if (leanRaw !== -1 && leanRaw !== 1) return null;
    out.lean = leanRaw;
  }

  // The mounted device: absent is dark; exactly the two literals the renderer
  // draws, else this entity is dropped.
  const tacRaw = x["tac"];
  if (tacRaw !== undefined) {
    if (tacRaw !== "lamp" && tacRaw !== "laser") return null;
    out.tac = tacRaw;
  }

  // The squad flag: exactly `true`. A literal `false` is a server we should
  // not believe about sides — a friendly diamond over an enemy gets somebody
  // killed.
  const teamRaw = x["team"];
  if (teamRaw !== undefined) {
    if (teamRaw !== true) return null;
    out.team = true;
  }
  // The scav role, like `tac`: absent is a Wilderer or a human; exactly the
  // five roles.
  const roleRaw = x["role"];
  if (roleRaw !== undefined) {
    if (
      roleRaw !== "wilderer" &&
      roleRaw !== "raider" &&
      roleRaw !== "sniper" &&
      roleRaw !== "guard" &&
      roleRaw !== "boss"
    ) {
      return null;
    }
    out.role = roleRaw;
  }
  // The species, on EVERY animal snapshot (the memo cannot suppress it — see
  // `messages.ts`). Not cross-checked against `kind`: `remotes.ts` only reads
  // it for an animal, and rejecting would drop a good player over an unused
  // field.
  const speciesRaw = x["species"];
  if (speciesRaw !== undefined) {
    const sp = species(speciesRaw);
    if (sp === null) return null;
    out.species = sp;
  }
  return out;
}

function snapshot(x: unknown): Snapshot | null {
  if (!isObj(x)) return null;
  const tick = num(x["tick"]);
  const lastAckSeq = num(x["lastAckSeq"]);
  if (tick === null || lastAckSeq === null) return null;

  const youRaw = x["you"];
  if (!isObj(youRaw)) return null;
  const pos = vec3(youRaw["pos"]);
  const vel = vec3(youRaw["vel"]);
  const st = stance(youRaw["stance"]);
  const stamina = num(youRaw["stamina"]);
  if (pos === null || vel === null || st === null || stamina === null) {
    return null;
  }
  const onGround = bool(youRaw["onGround"]);
  if (onGround === null) return null;

  // --- Combat: rejected as a whole, or half a health block shows full hp to a
  // player bleeding out.
  const hp = limbHp(youRaw["hp"]);
  const bleedMask = limbMask(youRaw["bleedMask"]);
  const fracMask = limbMask(youRaw["fracMask"]);
  const dead = bool(youRaw["dead"]);
  const wpn = weapon(youRaw["weapon"]);
  if (
    hp === null ||
    bleedMask === null ||
    fracMask === null ||
    dead === null ||
    wpn === null
  ) {
    return null;
  }

  const entsRaw = x["entities"];
  if (!Array.isArray(entsRaw)) return null;
  const entities: EntitySnap[] = [];
  for (const e of entsRaw) {
    const parsed = entitySnap(e);
    if (parsed !== null) entities.push(parsed);
  }

  const you: YouSnap = {
    pos,
    vel,
    onGround,
    stance: st,
    stamina,
    hp,
    bleedMask,
    fracMask,
    dead,
    weapon: wpn,
  };

  // Med cast (protocol v4). Absent = not casting, which is the common case and
  // the only meaning "no field" can have; present and nonsense is a rejection.
  const medEndTick = youRaw["medEndTick"];
  if (medEndTick !== undefined) {
    const v = num(medEndTick);
    if (v === null || v < 0) return null;
    you.medEndTick = v;
  }

  // Needs: absent means both meters full (a pre-v26 server, a fresh body);
  // present and nonsense rejects.
  const needsRaw = youRaw["needs"];
  if (needsRaw !== undefined) {
    if (!isObj(needsRaw)) return null;
    const energy = num(needsRaw["energy"]);
    const hydration = num(needsRaw["hydration"]);
    if (energy === null || hydration === null || energy < 0 || hydration < 0) {
      return null;
    }
    you.needs = { energy, hydration };
  }

  // Extraction: absent means "not in one". `secLeft` may be 0 (the last
  // snapshot).
  const extractRaw = youRaw["extract"];
  if (extractRaw !== undefined) {
    if (!isObj(extractRaw)) return null;
    const name = id(extractRaw["name"]);
    const secLeft = num(extractRaw["secLeft"]);
    if (name === null || secLeft === null || secLeft < 0) return null;
    you.extract = { name, secLeft };
  }

  const out: Snapshot = { tick, lastAckSeq, you, entities };

  // Tracks are ADVISORY scenery: a non-array is ignored and a malformed element
  // is dropped alone, since losing a frame of positions over a footprint is not
  // worth it.
  const tracksRaw = x["tracks"];
  if (Array.isArray(tracksRaw)) {
    const tracks: TrackSnap[] = [];
    for (const t of tracksRaw) {
      const parsed = trackSnap(t);
      if (parsed !== null) tracks.push(parsed);
    }
    if (tracks.length > 0) out.tracks = tracks;
  }

  return out;
}

/**
 * One mark on the ground, or `null`. `age` may be zero but not negative — a
 * mark from the future would never fade.
 */
function trackSnap(x: unknown): TrackSnap | null {
  if (!isObj(x)) return null;
  const tid = num(x["id"]);
  const k = x["k"];
  const pos = vec3(x["pos"]);
  const yaw = num(x["yaw"]);
  const age = num(x["age"]);
  if (tid === null || !isTrackKind(k) || pos === null) return null;
  if (yaw === null || age === null || age < 0) return null;
  return { id: tid, k, pos, yaw, age };
}

// ---------------------------------------------------------------------------
// Meta narrowing (protocol v6).
// ---------------------------------------------------------------------------

/**
 * One quest's progress. REPAIRED, not rejected: quest ids come from older
 * content, and a bad entry reads as "locked".
 */
function questState(x: unknown): QuestState | null {
  if (!isObj(x)) return null;
  const qid = id(x["id"]);
  const progress = uint(x["progress"]);
  const done = bool(x["done"]);
  if (qid === null || progress === null || done === null) return null;
  return { id: qid, progress, done };
}

function questStates(x: unknown): QuestState[] | null {
  if (!Array.isArray(x)) return null;
  const out: QuestState[] = [];
  for (const raw of x) {
    const q = questState(raw);
    if (q !== null) out.push(q);
  }
  return out;
}

/**
 * The meta state, whole. Kit and stash go through the same `inventory` /
 * `gridState` decoders as a raid's, so the all-or-nothing rule applies and a
 * bad profile is dropped (the previous stays up). The hideout, progression
 * and loyalty cannot fail the profile: their sanitizers never throw and repair
 * what they get — a missing one is an empty cellar, a level-1 account, a
 * stranger at every counter. The hideout's fallback `nowMs` is 0, not a local
 * clock: `lastAdvanceMs` is a SERVER instant.
 */
function profileWire(x: unknown): ProfileWire | null {
  if (!isObj(x)) return null;
  const name = x["name"];
  const euros = uint(x["euros"]);
  const stash = gridState(x["stash"]);
  const kit = inventory(x["kit"]);
  const quests = questStates(x["quests"]);
  if (typeof name !== "string" || euros === null || stash === null) return null;
  if (kit === null || quests === null) return null;
  return {
    name,
    euros,
    stash,
    kit,
    quests,
    hideout: sanitizeHideout(x["hideout"], 0),
    progress: sanitizeProgress(x["progress"]),
    loyalty: sanitizeLoyalty(x["loyalty"]),
  };
}

function raidResult(x: unknown): RaidResult | null {
  return x === "extracted" || x === "died" || x === "mia" ? x : null;
}

/** A level off the wire. Absent, garbage or a zero is level 1 — the ladder has
 *  no rung below it, and the summary only ever compares two of these. */
function wireLevel(x: unknown): number {
  const v = uint(x);
  return v === null || v < 1 ? 1 : v;
}

/**
 * The raid-end readout. `extractName`/`killedBy` are advisory (blank =
 * absent); `questsAdvanced` is repaired like quest states; the four v25
 * numbers default to zero and level 1. A readout without a line is still a
 * readout, and the only way back to the menu is the button under it.
 */
function raidSummary(x: unknown): RaidSummary | null {
  if (!isObj(x)) return null;
  const result = raidResult(x["result"]);
  const scavKills = uint(x["scavKills"]);
  const pmcKills = uint(x["pmcKills"]);
  const rewardEuros = uint(x["rewardEuros"]);
  const advancedRaw = x["questsAdvanced"];
  if (result === null || scavKills === null || pmcKills === null) return null;
  if (rewardEuros === null || !Array.isArray(advancedRaw)) return null;

  const questsAdvanced: string[] = [];
  for (const raw of advancedRaw) {
    const qid = id(raw);
    if (qid !== null) questsAdvanced.push(qid);
  }

  const out: RaidSummary = {
    result,
    scavKills,
    pmcKills,
    bossKills: uint(x["bossKills"]) ?? 0,
    questsAdvanced,
    rewardEuros,
    xpGained: uint(x["xpGained"]) ?? 0,
    levelBefore: wireLevel(x["levelBefore"]),
    levelAfter: wireLevel(x["levelAfter"]),
  };
  const extractName = id(x["extractName"]);
  if (extractName !== null) out.extractName = extractName;
  const killedBy = id(x["killedBy"]);
  if (killedBy !== null) out.killedBy = killedBy;
  return out;
}

// ---------------------------------------------------------------------------
// Social narrowing — the strictest policy here: `friends` and `partyState`
// are whole-state, so there is no repairing one; a bad message is dropped and
// the previous state stays up. Usernames are checked against the server's own
// registration rule (`isValidUsername`).
// ---------------------------------------------------------------------------

function username(x: unknown): string | null {
  return isValidUsername(x) ? x : null;
}

/**
 * A display name: 1..`MAX_NAME_LENGTH` characters, no control codes (a newline
 * would mangle every list).
 */
function displayName(x: unknown): string | null {
  if (typeof x !== "string" || x.length === 0 || x.length > MAX_NAME_LENGTH) return null;
  for (let i = 0; i < x.length; i++) {
    const c = x.charCodeAt(i);
    if (c < 0x20 || c === 0x7f) return null;
  }
  return x;
}

function socialPhase(x: unknown): SocialPhase | null {
  return x === "menu" || x === "queued" || x === "raid" ? x : null;
}

/** Username + display name, and nothing else: both ends of a pending request. */
function friendRef(x: unknown): FriendRef | null {
  if (!isObj(x)) return null;
  const uname = username(x["username"]);
  const name = displayName(x["name"]);
  if (uname === null || name === null) return null;
  return { username: uname, name };
}

function friendRefs(x: unknown): FriendRef[] | null {
  if (!Array.isArray(x)) return null;
  const out: FriendRef[] = [];
  for (const raw of x) {
    const ref = friendRef(raw);
    if (ref === null) return null;
    out.push(ref);
  }
  return out;
}

/**
 * One person on the list. `phase` is optional and may only ride with
 * `online` — "offline and in a raid" is not a state. An online friend without
 * one reads as "Online" and `canInvite` refuses them, the harmless direction.
 */
function friendEntry(x: unknown): FriendEntry | null {
  if (!isObj(x)) return null;
  const uname = username(x["username"]);
  const name = displayName(x["name"]);
  const online = bool(x["online"]);
  if (uname === null || name === null || online === null) return null;

  const rawPhase = x["phase"];
  if (rawPhase === undefined) return { username: uname, name, online };
  const phase = socialPhase(rawPhase);
  if (phase === null || !online) return null;
  return { username: uname, name, online, phase };
}

/**
 * One party member. `phase` is REQUIRED (the panel's job is readiness) and not
 * cross-checked against `online` — a dropped member keeps their last phase.
 */
function partyMember(x: unknown): PartyMemberWire | null {
  if (!isObj(x)) return null;
  const uname = username(x["username"]);
  const name = displayName(x["name"]);
  const online = bool(x["online"]);
  const phase = socialPhase(x["phase"]);
  const leader = bool(x["leader"]);
  if (uname === null || name === null || online === null) return null;
  if (phase === null || leader === null) return null;
  return { username: uname, name, online, phase, leader };
}

/**
 * `partyState.party`: the party, or `null` for "in none" (a meaning, not an
 * absence). An empty party is not a state.
 */
function partyOrNull(x: unknown): { ok: true; party: PartyStateMsg["party"] } | null {
  if (x === null) return { ok: true, party: null };
  if (!isObj(x)) return null;
  const raw = x["members"];
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const members: PartyMemberWire[] = [];
  for (const entry of raw) {
    const member = partyMember(entry);
    if (member === null) return null;
    members.push(member);
  }
  return { ok: true, party: { members } };
}

function gameEvent(x: unknown): GameEvent | null {
  if (!isObj(x)) return null;
  if (x["t"] === "playerJoined") {
    const id = x["id"];
    const name = x["name"];
    if (typeof id !== "string" || typeof name !== "string") return null;
    return { t: "playerJoined", id, name };
  }
  if (x["t"] === "playerLeft") {
    const id = x["id"];
    if (typeof id !== "string") return null;
    return { t: "playerLeft", id };
  }
  if (x["t"] === "shot") {
    const id = x["id"];
    const origin = vec3(x["origin"]);
    const dir = vec3(x["dir"]);
    if (typeof id !== "string" || origin === null || dir === null) return null;
    // `weaponId` is optional; blank or non-string is treated as absent (the
    // carbine fallback is already the right answer).
    const raw = x["weaponId"];
    const weaponId = typeof raw === "string" && raw.length > 0 ? raw : undefined;
    return weaponId === undefined
      ? { t: "shot", id, origin, dir }
      : { t: "shot", id, origin, dir, weaponId };
  }
  if (x["t"] === "hit") {
    const shooterId = x["shooterId"];
    const targetId = x["targetId"];
    const limb = limbId(x["limb"]);
    const damage = num(x["damage"]);
    if (
      typeof shooterId !== "string" ||
      typeof targetId !== "string" ||
      limb === null ||
      damage === null
    ) {
      return null;
    }
    return { t: "hit", shooterId, targetId, limb, damage };
  }
  if (x["t"] === "death") {
    const id = x["id"];
    const killerId = x["killerId"];
    const cause = deathCause(x["cause"]);
    if (typeof id !== "string" || typeof killerId !== "string" || cause === null) {
      return null;
    }
    // The forensics are decoration on a death that has already happened: a
    // malformed one is DROPPED FIELD BY FIELD, never the whole event.
    const ev: Extract<GameEvent, { t: "death" }> = { t: "death", id, killerId, cause };
    const killerName = x["killerName"];
    if (typeof killerName === "string") ev.killerName = killerName;
    const killerEye = vec3(x["killerEye"]);
    if (killerEye !== null) ev.killerEye = killerEye;
    const distM = num(x["distM"]);
    if (distM !== null && distM >= 0) ev.distM = distM;
    // `id()` is shadowed by this block's own `id`, so inline its rule.
    const weaponId = x["weaponId"];
    if (typeof weaponId === "string" && weaponId.length > 0) ev.weaponId = weaponId;
    const limb = limbId(x["limb"]);
    if (limb !== null) ev.limb = limb;
    return ev;
  }
  if (x["t"] === "itemDropped") {
    const item = worldItem(x);
    return item === null ? null : { t: "itemDropped", ...item };
  }
  if (x["t"] === "itemTaken") {
    const iid = id(x["id"]);
    return iid === null ? null : { t: "itemTaken", id: iid };
  }
  if (x["t"] === "grenadeThrown") {
    // Every field required: this event is all the client knows about the
    // grenade, and a defaulted velocity is an invented arc.
    const gid = id(x["id"]);
    const ownerId = x["ownerId"];
    const defId = id(x["defId"]);
    const pos = vec3(x["pos"]);
    const vel = vec3(x["vel"]);
    const explodeTick = num(x["explodeTick"]);
    if (
      gid === null ||
      typeof ownerId !== "string" ||
      defId === null ||
      pos === null ||
      vel === null ||
      explodeTick === null
    ) {
      return null;
    }
    return { t: "grenadeThrown", id: gid, ownerId, defId, pos, vel, explodeTick };
  }
  if (x["t"] === "grenadeExploded") {
    const gid = id(x["id"]);
    const defId = id(x["defId"]);
    const pos = vec3(x["pos"]);
    if (gid === null || defId === null || pos === null) return null;
    return { t: "grenadeExploded", id: gid, defId, pos };
  }
  if (x["t"] === "birdFlush") {
    const pos = vec3(x["pos"]);
    return pos === null ? null : { t: "birdFlush", pos };
  }
  if (x["t"] === "glass") {
    // The id is an INDEX into `map.glass`; the map is what bounds it, and
    // `GlassState.apply` does that check.
    const gid = uint(x["id"]);
    return gid === null ? null : { t: "glass", id: gid };
  }
  if (x["t"] === "bark") {
    // The kind is the whole event: an unknown one has no line to draw, and a
    // fractional variant is a frame from a protocol we do not speak.
    const sid = id(x["id"]);
    const kind = x["kind"];
    const v = num(x["v"]);
    if (sid === null || !isBarkKind(kind) || v === null) return null;
    if (!Number.isInteger(v) || v < 0) return null;
    return { t: "bark", id: sid, kind, v };
  }
  return null;
}

/** One entry of `worldItems`, and the payload of `itemDropped`. */
function worldItem(x: unknown): WorldItemSnap | null {
  if (!isObj(x)) return null;
  const wid = id(x["id"]);
  const defId = id(x["defId"]);
  const pos = vec3(x["pos"]);
  if (wid === null || defId === null || pos === null) return null;
  return { id: wid, defId, pos };
}

/**
 * Narrows `welcome.mapSource`. A download without URL or hash leaves no world
 * to stand in, so it is dropped.
 */
function welcomeMapSource(x: unknown): MapSource | null {
  if (!isObj(x)) return null;
  if (x["kind"] === "static") return { kind: "static" };
  if (x["kind"] === "download") {
    const url = x["url"];
    const hash = x["hash"];
    if (typeof url !== "string" || url.length === 0) return null;
    if (typeof hash !== "string" || hash.length === 0) return null;
    // Advisory (drives the download progress bar), so a bad value degrades to
    // "size unknown" instead of dropping an otherwise usable welcome.
    const bytes = x["bytes"];
    if (typeof bytes === "number" && Number.isFinite(bytes) && bytes > 0) {
      return { kind: "download", url, hash, bytes };
    }
    return { kind: "download", url, hash };
  }
  return null;
}

/**
 * `welcome.skillMods`. ADVISORY: a malformed record degrades to
 * `DEFAULT_SKILL_MODS` rather than refusing the raid. All four or none — a
 * record with a NaN is a server we do not understand, and splicing halves
 * would invent a body. `undefined` = absent (a pre-v25 server), not a failure.
 */
function welcomeSkillMods(x: unknown): SkillMods | undefined {
  if (x === undefined) return undefined;
  if (!isObj(x)) return DEFAULT_SKILL_MODS;
  const carryMult = mult(x["carryMult"]);
  const bleedChanceMult = mult(x["bleedChanceMult"]);
  const wearMult = mult(x["wearMult"]);
  const staminaRegenMult = mult(x["staminaRegenMult"]);
  if (carryMult === null || bleedChanceMult === null) return DEFAULT_SKILL_MODS;
  if (wearMult === null || staminaRegenMult === null) return DEFAULT_SKILL_MODS;
  return { carryMult, bleedChanceMult, wearMult, staminaRegenMult };
}

/**
 * A multiplier off the wire: finite and strictly above zero — a factor of
 * nothing is a body that never recovers.
 */
function mult(x: unknown): number | null {
  return typeof x === "number" && Number.isFinite(x) && x > 0 ? x : null;
}

export function asServerMsg(x: unknown): ServerMsg | null {
  if (!isObj(x)) return null;
  switch (x["t"]) {
    case "welcome": {
      const playerId = x["playerId"];
      const mapId = x["mapId"];
      const tick = num(x["tick"]);
      const seed = num(x["seed"]);
      const spawn = vec3(x["spawn"]);
      const mapSource = welcomeMapSource(x["mapSource"]);
      if (
        typeof playerId !== "string" ||
        typeof mapId !== "string" ||
        tick === null ||
        seed === null ||
        spawn === null ||
        mapSource === null
      ) {
        return null;
      }
      // `raidEndTick`: optional (direct servers never end), nonsense rejects.
      // `env`: missing is a pre-v24 server (`DEFAULT_ENV`); present and
      // malformed rejects, since a `time` with no preset would light the raid
      // with `undefined`.
      const rawEnv = x["env"];
      const env = rawEnv === undefined || isRaidEnv(rawEnv) ? rawEnv : null;
      if (env === null) return null;

      // What the player's skills are worth in here (protocol v25). Neither
      // absent nor malformed is a rejection — see `welcomeSkillMods`.
      const skillMods = welcomeSkillMods(x["skillMods"]);

      const raidEndTick = x["raidEndTick"];
      if (raidEndTick !== undefined) {
        const v = uint(raidEndTick);
        if (v === null) return null;
        return {
          t: "welcome",
          playerId,
          tick,
          mapId,
          mapSource,
          seed,
          spawn,
          raidEndTick: v,
          ...(env !== undefined ? { env } : {}),
          ...(skillMods !== undefined ? { skillMods } : {}),
        };
      }
      return {
        t: "welcome",
        playerId,
        tick,
        mapId,
        mapSource,
        seed,
        spawn,
        ...(env !== undefined ? { env } : {}),
        ...(skillMods !== undefined ? { skillMods } : {}),
      };
    }
    case "snapshot": {
      const snap = snapshot(x["snap"]);
      return snap === null ? null : { t: "snapshot", snap };
    }
    case "event": {
      const ev = gameEvent(x["ev"]);
      return ev === null ? null : { t: "event", ev };
    }
    case "pong": {
      const cNow = num(x["cNow"]);
      const sNow = num(x["sNow"]);
      if (cNow === null || sNow === null) return null;
      return { t: "pong", cNow, sNow };
    }
    case "error": {
      const code = x["code"];
      const msg = x["msg"];
      if (typeof code !== "string" || typeof msg !== "string") return null;
      return { t: "error", code, msg };
    }
    case "flash": {
      // Clamped rather than rejected: nonsense should cost a moment's white
      // screen, never a permanently blind one.
      const sec = num(x["sec"]);
      if (sec === null || sec <= 0) return null;
      return { t: "flash", sec: sec > MAX_FLASH_SEC ? MAX_FLASH_SEC : sec };
    }
    case "inv": {
      const inv = inventory(x["inv"]);
      return inv === null ? null : { t: "inv", inv };
    }
    case "containerState": {
      const cid = id(x["id"]);
      const k = containerKind(x["kind"]);
      const name = x["name"];
      const grid = gridState(x["grid"]);
      if (cid === null || k === null || typeof name !== "string" || grid === null) {
        return null;
      }
      // A body carries its worn slots. Absent is an ordinary crate (the key's
      // presence is what marks a corpse); present and malformed rejects the
      // message.
      const eqRaw = x["equipment"];
      if (eqRaw === undefined) return { t: "containerState", id: cid, kind: k, name, grid };
      const equipment = equipmentRecord(eqRaw);
      return equipment === null
        ? null
        : { t: "containerState", id: cid, kind: k, name, grid, equipment };
    }
    case "invResult": {
      const opId = uint(x["opId"]);
      const ok = bool(x["ok"]);
      if (opId === null || ok === null) return null;
      const reason = x["reason"];
      if (reason === undefined) return { t: "invResult", opId, ok };
      if (typeof reason !== "string") return null;
      return { t: "invResult", opId, ok, reason };
    }
    case "worldItems": {
      const raw = x["items"];
      if (!Array.isArray(raw)) return null;
      const items: WorldItemSnap[] = [];
      for (const entry of raw) {
        const item = worldItem(entry);
        if (item === null) return null;
        items.push(item);
      }
      return { t: "worldItems", items };
    }
    case "glassState": {
      const raw = x["broken"];
      if (!Array.isArray(raw)) return null;
      const broken: number[] = [];
      for (const entry of raw) {
        const gid = uint(entry);
        if (gid === null) return null;
        broken.push(gid);
      }
      return { t: "glassState", broken };
    }
    case "profile": {
      const profile = profileWire(x["profile"]);
      return profile === null ? null : { t: "profile", profile };
    }
    case "queueStatus": {
      const queued = uint(x["queued"]);
      if (queued === null) return null;
      // `null` is a MEANING here ("waiting for more players"), not an absence,
      // so it is accepted as-is and anything else has to be a real count.
      const raw = x["startsInMs"];
      if (raw === null) return { t: "queueStatus", queued, startsInMs: null };
      const startsInMs = num(raw);
      if (startsInMs === null || startsInMs < 0) return null;
      return { t: "queueStatus", queued, startsInMs };
    }
    case "raidEnd": {
      const summary = raidSummary(x["summary"]);
      return summary === null ? null : { t: "raidEnd", summary };
    }
    case "friends": {
      const raw = x["friends"];
      if (!Array.isArray(raw)) return null;
      const friends: FriendEntry[] = [];
      for (const entry of raw) {
        const friend = friendEntry(entry);
        if (friend === null) return null;
        friends.push(friend);
      }
      const incoming = friendRefs(x["incoming"]);
      const outgoing = friendRefs(x["outgoing"]);
      if (incoming === null || outgoing === null) return null;
      return { t: "friends", friends, incoming, outgoing };
    }
    case "partyState": {
      const party = partyOrNull(x["party"]);
      return party === null ? null : { t: "partyState", party: party.party };
    }
    case "partyInvited": {
      const from = friendRef(x["from"]);
      return from === null ? null : { t: "partyInvited", from };
    }
    default:
      return null;
  }
}
